import { useState, useMemo } from 'react';
import { trpc } from '../lib/trpc.js';
import { formatGbp } from '../lib/format.js';
import { clsx } from 'clsx';
import { format, startOfWeek, addDays, subWeeks, addWeeks, parse } from 'date-fns';

function thisMonday(): string {
  return format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
}

export function TimesheetsPage() {
  const [weekStart, setWeekStart] = useState(thisMonday());
  const week = trpc.timesheets.week.useQuery({ weekStart });
  const utils = trpc.useUtils();
  const upsert = trpc.timesheets.upsert.useMutation({
    onSuccess: () => utils.timesheets.week.invalidate({ weekStart }),
  });

  const days = useMemo(() => {
    const start = parse(weekStart, 'yyyy-MM-dd', new Date());
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(start, i);
      return { dayKey: format(d, 'yyyy-MM-dd'), label: format(d, 'EEE'), short: format(d, 'd MMM') };
    });
  }, [weekStart]);

  const cellsByKey = useMemo(() => {
    const m = new Map<string, { hours: number; notes: string | null }>();
    if (week.data) {
      for (const c of week.data.cells) {
        m.set(`${c.technicianName}|${c.dayKey}`, { hours: c.hours, notes: c.notes });
      }
    }
    return m;
  }, [week.data]);

  if (week.isLoading) return <p className="text-gray-500">Loading…</p>;
  if (!week.data) return <p className="text-gray-500">No data</p>;

  const techs = week.data.techs;

  // Aggregates
  const techWeekHours = (name: string) =>
    days.reduce((s, d) => s + (cellsByKey.get(`${name}|${d.dayKey}`)?.hours ?? 0), 0);
  const dayTotalHours = (dayKey: string) =>
    techs.reduce((s, t) => s + (cellsByKey.get(`${t.name}|${dayKey}`)?.hours ?? 0), 0);
  const techWeekCost = (name: string, rate: number | null) => {
    if (!rate) return null;
    return techWeekHours(name) * rate;
  };

  const grandHours = techs.reduce((s, t) => s + techWeekHours(t.name), 0);
  const grandCost = techs.reduce((s, t) => s + (techWeekCost(t.name, t.hourlyRateGbp) ?? 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="font-display text-3xl text-navy">Timesheets</h1>
          <p className="text-sm text-gray-500">
            Manual hours by technician × day. Saved on blur. Takes priority over Zenbooker times for cost calc.
          </p>
        </div>
        <div className="flex gap-1 items-center">
          <button
            onClick={() => setWeekStart(format(subWeeks(parse(weekStart, 'yyyy-MM-dd', new Date()), 1), 'yyyy-MM-dd'))}
            className="px-3 py-1.5 rounded-md text-xs font-medium border bg-white text-navy border-gray-200"
          >
            ← Prev
          </button>
          <button
            onClick={() => setWeekStart(thisMonday())}
            className="px-3 py-1.5 rounded-md text-xs font-medium border bg-white text-navy border-gray-200"
          >
            Today
          </button>
          <button
            onClick={() => setWeekStart(format(addWeeks(parse(weekStart, 'yyyy-MM-dd', new Date()), 1), 'yyyy-MM-dd'))}
            className="px-3 py-1.5 rounded-md text-xs font-medium border bg-white text-navy border-gray-200"
          >
            Next →
          </button>
          <span className="ml-3 text-sm font-mono text-gray-600">Week of {weekStart}</span>
        </div>
      </div>

      {techs.length === 0 ? (
        <div className="card border-amber-200 bg-amber-50/60">
          <p className="text-sm">
            No technicians to show. Either no jobs in this week and no rates configured, or all techs are excluded.
            Visit <span className="font-mono">/technicians</span> to add or unexclude.
          </p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-500">
                <th className="text-left py-2 px-3 font-medium">Technician</th>
                <th className="text-right py-2 px-2 font-medium font-mono">£/hr</th>
                {days.map((d) => (
                  <th key={d.dayKey} className="text-center py-2 px-2 font-medium">
                    <div>{d.label}</div>
                    <div className="text-gray-400 font-normal">{d.short}</div>
                  </th>
                ))}
                <th className="text-right py-2 px-3 font-medium">Total hrs</th>
                <th className="text-right py-2 px-3 font-medium">Cost</th>
                <th className="text-center py-2 px-3 font-medium">Payslip</th>
              </tr>
            </thead>
            <tbody>
              {techs.map((t) => (
                <tr key={t.name} className="border-b border-gray-50 hover:bg-cream-tint">
                  <td className="py-2 px-3 font-medium text-navy">
                    {t.name}
                    {!t.rateConfigured && <span className="ml-2 text-xs text-amber-600">no rate</span>}
                  </td>
                  <td className="py-2 px-2 text-right font-mono">{t.hourlyRateGbp ? `£${t.hourlyRateGbp.toFixed(2)}` : '—'}</td>
                  {days.map((d) => (
                    <HoursCell
                      key={d.dayKey}
                      tech={t.name}
                      dayKey={d.dayKey}
                      initial={cellsByKey.get(`${t.name}|${d.dayKey}`)?.hours ?? null}
                      onSave={(hours) =>
                        upsert.mutate({ technicianName: t.name, date: d.dayKey, hours, notes: null })
                      }
                      saving={upsert.isPending}
                    />
                  ))}
                  <td className="py-2 px-3 text-right font-mono">{techWeekHours(t.name) > 0 ? techWeekHours(t.name).toFixed(2) : '—'}</td>
                  <td className="py-2 px-3 text-right font-mono text-navy">
                    {techWeekCost(t.name, t.hourlyRateGbp) !== null && techWeekCost(t.name, t.hourlyRateGbp)! > 0
                      ? formatGbp(techWeekCost(t.name, t.hourlyRateGbp)!)
                      : '—'}
                  </td>
                  <td className="py-2 px-3 text-center">
                    {techWeekHours(t.name) > 0 ? (
                      <a
                        href={`/payslip?tech=${encodeURIComponent(t.name)}&weekStart=${weekStart}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs px-2 py-1 rounded bg-navy text-white hover:bg-navy-soft"
                      >
                        Open
                      </a>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-200 font-medium text-navy bg-cream-tint/50">
                <td className="py-2 px-3">Total</td>
                <td className="py-2 px-2"></td>
                {days.map((d) => (
                  <td key={d.dayKey} className="py-2 px-2 text-center font-mono">
                    {dayTotalHours(d.dayKey) > 0 ? dayTotalHours(d.dayKey).toFixed(2) : '—'}
                  </td>
                ))}
                <td className="py-2 px-3 text-right font-mono">{grandHours > 0 ? grandHours.toFixed(2) : '—'}</td>
                <td className="py-2 px-3 text-right font-mono">{grandCost > 0 ? formatGbp(grandCost) : '—'}</td>
                <td className="py-2 px-3 text-center"></td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-gray-400 mt-3">
            Type a number into any cell and click away to save. Empty / 0 hours removes the entry.
            When no manual entry exists for a (tech, day), Zenbooker timestamps fall back if available.
          </p>
        </div>
      )}
    </div>
  );
}

function HoursCell({
  tech, dayKey, initial, onSave, saving,
}: {
  tech: string;
  dayKey: string;
  initial: number | null;
  onSave: (hours: number) => void;
  saving: boolean;
}) {
  const [value, setValue] = useState(initial !== null ? String(initial) : '');
  const [pristine, setPristine] = useState(true);

  const commit = () => {
    if (pristine) return;
    const parsed = value.trim() === '' ? 0 : parseFloat(value);
    if (isNaN(parsed) || parsed < 0 || parsed > 24) return;
    onSave(parsed);
    setPristine(true);
  };

  // Sync from server-side state if the cell hasn't been edited locally
  if (pristine && (initial !== null ? String(initial) : '') !== value) {
    // we don't auto-overwrite once user typed; this branch only matches initial render
  }

  return (
    <td className="py-1 px-1 text-center" title={`${tech} on ${dayKey}`}>
      <input
        type="number"
        step="0.25"
        min="0"
        max="24"
        placeholder="—"
        value={value}
        onChange={(e) => { setValue(e.target.value); setPristine(false); }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          }
        }}
        disabled={saving}
        className={clsx(
          'w-14 px-1 py-1 rounded border text-right font-mono text-sm',
          pristine ? 'border-gray-100' : 'border-gold',
          parseFloat(value) > 0 && 'bg-white',
        )}
      />
    </td>
  );
}
